import { Box, Typography, Button, TextField, MenuItem, useTheme } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import moment from "moment";
import { DATE_FILTERS } from "api/hooks/shared/paginatedList";
import DateCalendarPicker from "components/DateCalendarPicker";

const PERIOD_OPTIONS = [
  { label: "Today", value: DATE_FILTERS.today },
  { label: "Yesterday", value: DATE_FILTERS.yesterday },
  { label: "This Month", value: DATE_FILTERS.this_month },
  { label: "This Year", value: DATE_FILTERS.this_year },
  { label: "Custom", value: DATE_FILTERS.custom },
];

export default function ListDateFilterBar({
  dateFilter,
  customRange,
  onFilterChange,
  onCustomRangeChange,
  onCustomApply,
  embedded = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent = theme.palette.secondary.main;
  const textSec = theme.palette.neutral.main;
  const border = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const showCustom = dateFilter === DATE_FILTERS.custom;
  const today = moment().format("YYYY-MM-DD");
  const startMaxDate = customRange.endDate
    ? moment.min(moment(customRange.endDate), moment(today)).format("YYYY-MM-DD")
    : today;

  const selectSx = {
    minWidth: "180px",
    "& .MuiInputBase-root": {
      fontSize: "13px",
      height: "36px",
      backgroundColor: cardBg,
      borderRadius: "8px",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
    "& .MuiInputLabel-root": { fontSize: "12px" },
  };

  const content = (
    <>
      <TextField
        select
        label="Date filter"
        size="small"
        value={dateFilter}
        onChange={(e) => onFilterChange(e.target.value)}
        sx={selectSx}
      >
        {PERIOD_OPTIONS.map((p) => (
          <MenuItem key={p.value} value={p.value} sx={{ fontSize: "13px" }}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>

      {showCustom && (
        <Box sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
          pl: embedded ? 0 : "0.5rem",
          borderLeft: embedded ? "none" : `1px solid ${border}`,
        }}>
          <DateCalendarPicker
            label="Start date"
            value={customRange.startDate}
            onChange={(startDate) => onCustomRangeChange({ ...customRange, startDate })}
            maxDate={startMaxDate}
          />
          <Typography sx={{ fontSize: "12px", color: textSec }}>to</Typography>
          <DateCalendarPicker
            label="End date"
            value={customRange.endDate}
            onChange={(endDate) => onCustomRangeChange({ ...customRange, endDate })}
            minDate={customRange.startDate || undefined}
            maxDate={today}
          />
          <Button
            onClick={onCustomApply}
            disabled={!customRange.startDate || !customRange.endDate}
            sx={{
              fontSize: "12px", fontWeight: 500,
              px: "1rem", py: "0.35rem", borderRadius: "8px",
              backgroundColor: accent, color: "#fff", textTransform: "none",
              "&:hover": { backgroundColor: "#0F6E56" },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            Apply
          </Button>
        </Box>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <Box sx={{
      display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
      p: "0.75rem 1rem",
      backgroundColor: cardBg,
      border: `1px solid ${border}`,
      borderRadius: "10px",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
        <FilterListIcon sx={{ fontSize: "16px" }} />
        <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Period
        </Typography>
      </Box>
      {content}
    </Box>
  );
}
